import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import slugify from 'dibs-slugify/exports/slugify';
import HpRespCollectionsModuleItem from './HpRespCollectionsModuleItem';
import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { RenderIn, device } from '../../utils/RenderIn';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import {
    trackCollectionsImpression,
    trackCollectionsItemClick,
    trackArrowClick,
} from './helpers/CollectionsTracking';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import styles from './HpRespCollectionsModule.scss';

import { HpRespCollectionsModule_componentModule } from './__generated__/HpRespCollectionsModule_componentModule.graphql';

type CollectionItems = NonNullable<HpRespCollectionsModule_componentModule['items']>;

type Props = {
    componentModule: HpRespCollectionsModule_componentModule;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpRespCollectionsModule: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { title } = componentModule;
    const dataTn = `${slugify(title)}-container`;
    const items = (componentModule.items || []).filter(filterNulls);

    return (
        <HpRespModuleContainer className={styles.wrapper} dataTn={dataTn} addTopGap={!title}>
            <h2 className={styles.subHeader}>{title}</h2>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {() => {
                    const itemsPerPage = 5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({
                                visibleItems,
                                index,
                            }: {
                                visibleItems: CollectionItems;
                                index: number;
                            }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <Carousel
                                    slideCarouselItems
                                    totalItems={items.length}
                                    itemsPerPage={itemsPerPage}
                                    step={itemsPerPage}
                                    renderItem={({ index }: { index: number }) => (
                                        <HpRespCollectionsModuleItem
                                            item={items[index]}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() => {
                                                trackCollectionsItemClick({
                                                    item: items[index],
                                                    index,
                                                });
                                                trackModuleLocation({
                                                    label: 'small item carousel module',
                                                    moduleIndex,
                                                    totalModules,
                                                });
                                            }}
                                        />
                                    )}
                                    onIndexChange={handleIndexChange}
                                    onLeftArrowClick={() => trackArrowClick({ type: 'previous' })}
                                    onRightArrowClick={() => trackArrowClick({ type: 'next' })}
                                />
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 2.5;
                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({
                                visibleItems,
                                index,
                            }: {
                                visibleItems: CollectionItems;
                                index: number;
                            }) =>
                                trackCollectionsImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({
                                handlePageChange,
                            }: {
                                handlePageChange: (page: number) => void;
                            }) => (
                                <Swiper
                                    classNames={{ list: styles.swiperList }}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={(currentPage: number, previousPage: number) => {
                                        trackArrowClick({
                                            type: previousPage > currentPage ? 'previous' : 'next',
                                        });
                                        handlePageChange(currentPage);
                                    }}
                                >
                                    {items.map((item, index) => (
                                        <HpRespCollectionsModuleItem
                                            key={item.cmsId}
                                            item={item}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() => {
                                                trackCollectionsItemClick({ item, index });
                                                trackModuleLocation({
                                                    label: 'small item carousel module',
                                                    moduleIndex,
                                                    totalModules,
                                                });
                                            }}
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainer>
    );
};

export default createFragmentContainer(HpRespCollectionsModule, {
    componentModule: graphql`
        fragment HpRespCollectionsModule_componentModule on CollectionsModule {
            title
            items {
                cmsId
                title # used in tracking
                ...HpRespCollectionsModuleItem_item
            }
        }
    `,
});
